import React, { useEffect, useMemo, useRef, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayGridPlugin from "@fullcalendar/daygrid";
import multiMonthPlugin from '@fullcalendar/multimonth'
import { DatesSetArg, DateSelectArg, DayCellMountArg, EventApi, EventClickArg, EventContentArg } from '@fullcalendar/core';
import { Event } from "../../../../molecules/Calendar/Calendar.type";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
} from '@mui/material';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import SickIcon from '@mui/icons-material/Sick';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import AbsenceDialog from '../../../../molecules/Calendar/AbsenceDialog';
import { CalendarContext } from '../../../../stores/Calendar/calendar.provider';
import { AppointmentDto, DeleteLeaveOfAbsenceResultDto, LeaveOfAbsenceDto } from '../../../../api-client';
import useStore from '../../../../helpers/useStore';
import { useApi } from '../../../../api/ApiProvider';
import { mobiliTheme } from '../../../../themes/mobiliTheme';
import notificationStore from '../../../../stores/Notification/notificationStore';
import { useAbsenceConflictWatcher } from '../../../../hooks/useAbsenceConflictWatcher';
import { AbsencePeriod } from '../../../../molecules/Calendar/absenceOverlap';
import UserCalendarHeader from './UserCalendarHeader';
import FlatCard from '../../../../atoms/FlatCard';
import { getAbsenceTypeLabel, normalizeAbsenceType } from './absenceTypes';
import './UserCalendar.css';
import { extendMoment } from 'moment-range';
import moment, * as Moments from "moment";
const momentRange = extendMoment(Moments);

interface UserCalendarProps {
  userData: any;
}

// FullCalendar mutates every event sharing an id as a single group, so ids must never collide
// across absences and the read-only appointment background events.
const APPOINTMENT_EVENT_PREFIX = 'appointment-';
const UNSAVED_ABSENCE_PREFIX = 'unsaved-absence-';

const isPersistedAbsenceId = (id?: string) =>
  !!id && !id.startsWith(APPOINTMENT_EVENT_PREFIX) && !id.startsWith(UNSAVED_ABSENCE_PREFIX);

// Fallback entitlement for employees without an explicit `vacationDays` on their record.
const DEFAULT_VACATION_DAYS_PER_YEAR = 30;

const CONFLICT_ACTION_LABELS: Record<string, string> = {
  Create: 'hinzugefügt',
  Update: 'geändert',
  Delete: 'gelöscht',
};

// Events carry the raw `loAType` as title, so the calendar has to translate it for display.

const getAbsenceIcon = (type?: string) => {
  const id = normalizeAbsenceType(type);
  if (id.startsWith('sickness')) {
    return <SickIcon />;
  }
  if (id.includes('vacation') || id === 'special_leave') {
    return <BeachAccessIcon />;
  }
  return <EventBusyIcon />;
};

const UserCalendar: React.FC<UserCalendarProps> = ({ userData }) => {

  const calendarRef = useRef<FullCalendar>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // Deep links from the absence overview point at the month holding that absence.
  const initialDate = searchParams.get('absenceDate') || moment(new Date()).format("YYYY-MM-DD");
  const [event, setEvent] = useState<Event>();
  const [events, setEvents] = useState<Event[]>([]);
  const [appointments, setAppointments] = useState<Event[]>([]);
  const [appointmentDays, setAppointmentDays] = useState<string[]>([]);
  const [absencePeriods, setAbsencePeriods] = useState<AbsencePeriod[]>([]);
  const [isAddAbsenceDialogOpen, setAddAbsenceDialogOpen] = useState(false);
  const [calendarTitle, setCalendarTitle] = useState('');
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [displayedYear, setDisplayedYear] = useState(moment(initialDate).year());
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number;
    mouseY: number;
    date: string;
  } | null>(null);
  const [absenceToDelete, setAbsenceToDelete] = useState<{ id: string; title: string } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteResult, setDeleteResult] = useState<DeleteLeaveOfAbsenceResultDto | null>(null);
  const CalendarStore = useStore(CalendarContext);
  const { getUserAbsences, getNextAppointmentsByUserId } = CalendarStore;
  const { scheduler } = useApi();
  const userId: string | undefined = userData?.id;
  const vacationDaysPerYear: number = userData?.vacationDays ?? DEFAULT_VACATION_DAYS_PER_YEAR;
  const isTherapist: boolean = !!userData?.roles?.some((role: any) => role?.id === 'therapist');
  // Guards against a slower response for a previously selected employee overwriting the current one.
  const fetchTokenRef = useRef(0);
  // Another user editing the same employee's absences makes what we render stale.
  const { conflict, hasConflict, clearConflict } = useAbsenceConflictWatcher(userId);

  moment.locale('de');

  useEffect(() => {
    if (!hasConflict) {
      return;
    }
    // Nothing may be saved from a stale view, so tear down every open editing surface.
    setAddAbsenceDialogOpen(false);
    setEvent(undefined);
    setContextMenu(null);
    setAbsenceToDelete(null);
  }, [hasConflict]);

  const blockWhileStale = () => {
    if (!hasConflict) {
      return false;
    }
    notificationStore.showMessage(
      "Die Abwesenheiten wurden zwischenzeitlich von einem anderen Benutzer geändert. Bitte laden Sie den Kalender neu.",
      "warning"
    );
    return true;
  };

  const handleReloadAfterConflict = async () => {
    clearConflict();
    await fetchEvents();
  };

  const conflictPeriod = conflict?.start
    ? [conflict.start, conflict.end]
        .filter(Boolean)
        .map((d) => moment(d).format('DD.MM.YYYY'))
        .join(' – ')
    : '';

  // Keyed on the user id. With `[]` this fetched once per mount, so if the view
  // ever switched user without remounting, `events`/`absences` stayed on the
  // previous user while `handleConfirmDelete` addressed them against the new
  // `userData.id` — writing to the wrong records.
  useEffect(() => {
    activeUserIdRef.current = userData?.id;
    // Drop every piece of per-employee state, otherwise a stale `event` would make the dialog
    // update the previously selected employee's absence.
    setEvent(undefined);
    setEvents([]);
    setAbsencePeriods([]);
    setAppointments([]);
    setAppointmentDays([]);
    setAddAbsenceDialogOpen(false);
    setContextMenu(null);
    setAbsenceToDelete(null);

    if (!userId) {
      fetchTokenRef.current++;
      return;
    }
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const getDatesBetween = (start: moment.Moment, end: moment.Moment) => {
    const range = momentRange.range(start, end);
    const dates = Array.from(range.by('day'));
    return dates;
  };

  // Tracks whose calendar is currently on screen, so an in-flight fetch for a
  // previously selected user can be discarded instead of overwriting state.
  const activeUserIdRef = useRef<string | undefined>(userData?.id);

  const fetchEvents = async () => {
    if (!userId) {
      return;
    }
    const token = ++fetchTokenRef.current;
    try {
      const leaveOfAbsences = await getUserAbsences(userId);

      if (activeUserIdRef.current !== userId) return;

      const absenceEvents = leaveOfAbsences.map((absence: LeaveOfAbsenceDto, index: number) => {
        const event: Event = {
          id: absence.id || `${UNSAVED_ABSENCE_PREFIX}${index}`,
          title: absence.loAType || "",
          start: moment(absence.start!).format('YYYY-MM-DD'),
          end: moment(absence.end!).subtract(1, 'day').format('YYYY-MM-DDT01:00:00'),
          type: absence.loAType,
          color: "#cccccc",
          editable: false,
        };
        return event;
      });

      const appointments = await getNextAppointmentsByUserId(userId);
      if (activeUserIdRef.current !== userId) return;
      // console.log("appointments", appointments);
      
      const appointmentDayList = appointments
        .filter((app: AppointmentDto) => !!app.start)
        .map((app: AppointmentDto) => moment(app.start).format('YYYY-MM-DD'));

      const appointmentCountByDay = appointmentDayList.reduce<Record<string, number>>((acc, day) => {
        acc[day] = (acc[day] ?? 0) + 1;
        return acc;
      }, {});

      const appointmentEvents: Event[] = Object.entries(appointmentCountByDay)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([day, count], index) => ({
          id: `${APPOINTMENT_EVENT_PREFIX}${index}`,
          title: `${count} ${count === 1 ? 'Termin' : 'Termine'}`,
          start: day,
          end: day,
          display: "background",
          color: "white",
          editable: false,
        }));

      if (token !== fetchTokenRef.current) {
        return;
      }

      setAbsencePeriods(
        leaveOfAbsences
          .filter((absence: LeaveOfAbsenceDto) => !!absence.id)
          .map((absence: LeaveOfAbsenceDto) => ({
            id: absence.id!,
            loAType: absence.loAType,
            comment: absence.comment,
            start: moment(absence.start!).format('YYYY-MM-DD'),
            // The stored end is exclusive; the split works with the last absent day.
            end: moment(absence.end!).subtract(1, 'day').format('YYYY-MM-DD'),
          }))
      );
      setAppointments(appointmentEvents);
      setAppointmentDays(appointmentDayList);
      setEvents([...absenceEvents, ...appointmentEvents]);

    } catch (error) {
      // console.log("error", error);
    }
  }

  const handleDateSelect = (selectInfo: DateSelectArg) => {
    if (blockWhileStale()) {
      return;
    }
    // No id: this is a brand new absence, never an update of an existing one.
    const event: Event = {
      title: "Urlaub",
      start: moment(selectInfo.start).format('YYYY-MM-DD'),
      end: moment(selectInfo.end).add(-1, 'day').format('YYYY-MM-DD'),
    };
    setEvent(event);
    setAddAbsenceDialogOpen(true);
  };

  const openAbsenceForEditing = (absenceEvent: EventApi) => {
    setEvent({
      id: absenceEvent.id,
      title: absenceEvent.title,
      start: moment(absenceEvent.start).format('YYYY-MM-DD'),
      // The rendered end already is the last absent day; the dialog edits inclusive days.
      end: moment(absenceEvent.end).format('YYYY-MM-DD'),
      type: absenceEvent.extendedProps.type,
    });
    setAddAbsenceDialogOpen(true);
  };

  const handleEventClick = (clickInfo: EventClickArg) => {
    if (!isPersistedAbsenceId(clickInfo.event.id) || blockWhileStale()) {
      return;
    }
    openAbsenceForEditing(clickInfo.event);
  };
  
  const handleOpenAddAbsenceDialog = () => {
    if (blockWhileStale()) {
      return;
    }
    setEvent(undefined);
    setAddAbsenceDialogOpen(true);
  };

  // The header is rendered by us, so it has to be kept in sync with the calendar's own state.
  const handleDatesSet = (arg: DatesSetArg) => {
    setCalendarTitle(arg.view.title);
    setCurrentView(arg.view.type);
    setDisplayedYear(arg.view.currentStart.getFullYear());
  };

  // The calendar store hydrates from the URL only once per app boot, so the date has to be
  // pushed into the store; the projection then writes it back to the URL.
  const handleNavLinkDayClick = (navDate: Date) => {
    CalendarStore.setSelectedDateStr(moment(navDate).format('YYYY-MM-DD'));
    navigate('/termin');
  };

  // Absence days are counted in working days, so weekends inside an absence are not deducted.
  const absenceSummary = useMemo(() => {
    const countWorkingDays = (loAType: string) =>
      absencePeriods
        .filter((absence) => normalizeAbsenceType(absence.loAType) === loAType)
        .reduce((days, absence) => {
          const from = moment.max(moment(absence.start), moment(`${displayedYear}-01-01`));
          const to = moment.min(moment(absence.end), moment(`${displayedYear}-12-31`));
          if (to.isBefore(from)) {
            return days;
          }
          return (
            days +
            getDatesBetween(from, to).filter((day) => day.isoWeekday() <= 5).length
          );
        }, 0);

    const vacation = countWorkingDays('vacation');
    const sickWithNote = countWorkingDays('sickness_sick_note');
    const sickWithoutNote = countWorkingDays('sickness');
    const childSickWithNote = countWorkingDays('sickness_child_sick_note');
    const childSickWithoutNote = countWorkingDays('sickness_child');

    return {
      vacation,
      remainingVacation: countWorkingDays('remaining_vacation'),
      open: vacationDaysPerYear - vacation,
      sickWithNote,
      sickWithoutNote,
      // The "with AU" days are part of the total, never counted on top of it.
      sickTotal: sickWithNote + sickWithoutNote,
      childSickWithNote,
      childSickWithoutNote,
      childSickTotal: childSickWithNote + childSickWithoutNote,
    };
  }, [absencePeriods, displayedYear, vacationDaysPerYear]);

  const handlePrev = () => calendarRef.current?.getApi().prev();

  const handleNext = () => calendarRef.current?.getApi().next();

  const handleToday = () => calendarRef.current?.getApi().today();

  const handleViewChange = (view: string) => calendarRef.current?.getApi().changeView(view);

  const handleEvents = (events: EventApi[]) => {

  };

  const onAbsenceDialogClose = () => {
    setAddAbsenceDialogOpen(false);
    setEvent(undefined);
  }

  const onAbsenceDialogConfirm = () => {
    setAddAbsenceDialogOpen(false);
    setEvent(undefined);
    fetchEvents();
  }

  // Attach a right-click context menu to every day cell; right-clicking an absence bubbles up
  // to its day cell, so the menu knows which day was hit.
  const handleDayCellDidMount = (arg: DayCellMountArg) => {
    arg.el.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      setContextMenu({
        mouseX: e.clientX + 2,
        mouseY: e.clientY - 6,
        date: moment(arg.date).format('YYYY-MM-DD'),
      });
    });
  };

  const renderEventContent = (arg: EventContentArg) => {
    if (arg.event.display === 'background') {
      return <div className="user-calendar-bg-event">{arg.event.title}</div>;
    }

    const type = arg.event.extendedProps.type as string | undefined;
    const label = getAbsenceTypeLabel(type ?? arg.event.title);
    const start = arg.event.start ? moment(arg.event.start).format('DD.MM.YYYY') : '';
    const end = arg.event.end ? moment(arg.event.end).format('DD.MM.YYYY') : '';
    const period = end && end !== start ? `${start} – ${end}` : start;

    return (
      <Tooltip title={period ? `${label} (${period})` : label}>
        <div className="user-calendar-absence">
          {getAbsenceIcon(type)}
          <span className="user-calendar-absence-label">{label}</span>
        </div>
      </Tooltip>
    );
  };

  const handleCloseContextMenu = () => {
    setContextMenu(null);
  };

  const contextAbsence = contextMenu
    ? absencePeriods.find(
        (absence) => contextMenu.date >= absence.start && contextMenu.date <= absence.end
      )
    : undefined;

  const contextAbsenceLabel = contextAbsence ? getAbsenceTypeLabel(contextAbsence.loAType) : '';

  const handleEditClick = () => {
    // Read the event back from the calendar so its current dates are edited.
    const absenceEvent =
      contextAbsence && calendarRef.current?.getApi().getEventById(contextAbsence.id);
    if (absenceEvent && !blockWhileStale()) {
      openAbsenceForEditing(absenceEvent);
    }
    setContextMenu(null);
  };

  const handleDeleteClick = () => {
    if (contextAbsence && !blockWhileStale()) {
      setAbsenceToDelete({ id: contextAbsence.id, title: contextAbsenceLabel });
    }
    setContextMenu(null);
  };

  const handleAddAbsenceOnDate = () => {
    const date = contextMenu?.date;
    setContextMenu(null);
    if (!date || blockWhileStale()) {
      return;
    }
    setEvent({ title: 'Urlaub', start: date, end: date });
    setAddAbsenceDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setAbsenceToDelete(null);
  };

  const handleCloseDeleteResultDialog = () => {
    setDeleteResult(null);
  };

  const handleConfirmDelete = async () => {
    if (!absenceToDelete || blockWhileStale()) {
      return;
    }
    try {
      setIsDeleting(true);
      // Deleting the absence puts back the appointments it cancelled, so the outcome has to be reported.
      const result = await scheduler.schedulerDeleteLeaveOfAbsence(absenceToDelete.id);
      const restoredCount = result?.restored?.length ?? 0;
      setAbsenceToDelete(null);
      await fetchEvents();
      if (result?.skipped?.length) {
        setDeleteResult(result);
      } else {
        notificationStore.showMessage(
          restoredCount > 0
            ? `Abwesenheit gelöscht. ${restoredCount} ${restoredCount === 1 ? 'Termin wurde' : 'Termine wurden'} wiederhergestellt.`
            : "Abwesenheit erfolgreich gelöscht",
          "success"
        );
      }
    } catch (error) {
      notificationStore.showMessage("Fehler beim Löschen der Abwesenheit", "error", error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <>
      <UserCalendarHeader
        title={calendarTitle}
        currentView={currentView}
        userData={userData}
        isAddDisabled={hasConflict}
        onPrev={handlePrev}
        onNext={handleNext}
        onToday={handleToday}
        onViewChange={handleViewChange}
        onAddAbsence={handleOpenAddAbsenceDialog}
      />
      {isAddAbsenceDialogOpen && 
      <AbsenceDialog
        key={event?.id ?? 'new-absence'}
        leaveOfAbsence = {{
          user: {id: userData.id}, 
          start: event?.start,
          end: event?.end,
          loAType: event?.type || "vacation",
        }}
        open={isAddAbsenceDialogOpen}
        event={event}
        onClose={onAbsenceDialogClose}
        title={event?.id ? "Abwesenheit bearbeiten" : "Abwesenheit hinzufügen"}
        onConfirm={onAbsenceDialogConfirm}
        existingAbsences={absencePeriods}
        appointmentDays={appointmentDays}
        isTherapist={isTherapist}
        highlightDays={appointments.map(app => moment(app.start))}
        allowPastDates={true}
      />}
      <Grid container spacing={2}>
        <Grid item xs={12} md={9}>
          <div className="user-calendar">
            <FullCalendar
              ref={calendarRef}
              locale={"de"}
              displayEventTime={false}
              initialDate={initialDate}
              schedulerLicenseKey="0067980715-fcs-1697454050"
              plugins={[
                dayGridPlugin,
                multiMonthPlugin,
                interactionPlugin
              ]}
              headerToolbar={false}
              titleFormat={{ year: "numeric", month: "long" }}
              initialView="dayGridMonth"
              firstDay={1}
              editable={false}
              selectable={!hasConflict}
              selectConstraint={undefined}
              selectMirror={true}
              dayMaxEvents={true}
              droppable={false}
              allDaySlot={true}
              navLinks={true}
              navLinkDayClick={handleNavLinkDayClick}
              events={events}
              select={handleDateSelect}
              eventClick={handleEventClick}
              eventsSet={handleEvents} // called after events are initialized/added/changed/removed
              dayCellDidMount={handleDayCellDidMount}
              eventContent={renderEventContent}
              datesSet={handleDatesSet}
            />
          </div>
        </Grid>
        <Grid item xs={12} md={3}>
          <FlatCard title={`Abwesenheiten ${displayedYear}`}>
            <Grid container spacing={3} direction="column">
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Urlaub genommen:
                </Typography>
                <Typography variant="h5" color="black" fontWeight="medium">
                  {absenceSummary.vacation} von {vacationDaysPerYear} Tagen
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Urlaubsanspruch: {vacationDaysPerYear} Tage
                  {userData?.vacationDays == null && ' (Standard)'}
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Resturlaub genommen:
                </Typography>
                <Typography variant="h5" color="info.main" fontWeight="medium">
                  {absenceSummary.remainingVacation} Tage
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Noch offen:
                </Typography>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={absenceSummary.open < 0 ? "error.main" : undefined}
                >
                  {absenceSummary.open} Tage
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Krank gesamt:
                </Typography>
                <Typography variant="h5" color="black" fontWeight="medium">
                  {absenceSummary.sickTotal} Tage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  davon mit AU: {absenceSummary.sickWithNote} Tage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  davon ohne AU: {absenceSummary.sickWithoutNote} Tage
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant="body2" color="text.secondary">
                  Kindkrank gesamt:
                </Typography>
                <Typography variant="h5" color="black" fontWeight="medium">
                  {absenceSummary.childSickTotal} Tage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  davon mit KAU: {absenceSummary.childSickWithNote} Tage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  davon ohne KAU: {absenceSummary.childSickWithoutNote} Tage
                </Typography>
              </Grid>
            </Grid>
          </FlatCard>
        </Grid>
      </Grid>
      <Menu
        open={contextMenu !== null}
        onClose={handleCloseContextMenu}
        anchorReference="anchorPosition"
        anchorPosition={
          contextMenu !== null
            ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
            : undefined
        }
      >
        {contextAbsence
          ? [
              <MenuItem key="edit" onClick={handleEditClick}>
                Bearbeiten
              </MenuItem>,
              <MenuItem key="delete" onClick={handleDeleteClick}>
                Löschen
              </MenuItem>,
            ]
          : <MenuItem onClick={handleAddAbsenceOnDate}>Abwesenheit hinzufügen</MenuItem>}
      </Menu>
      <Dialog open={absenceToDelete !== null} onClose={handleCloseDeleteDialog}>
        <DialogTitle>Abwesenheit löschen</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sind Sie sicher, dass Sie den Eintrag '{absenceToDelete?.title}' löschen möchten?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Die durch diese Abwesenheit abgesagten Termine werden dabei wiederhergestellt, sofern
            ihre ursprüngliche Zeit noch frei ist. Termine, die nicht wiederhergestellt werden
            können, werden Ihnen anschließend aufgelistet.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} variant="text" disabled={isDeleting}>
            Abbrechen
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deleteResult !== null} onClose={handleCloseDeleteResultDialog}>
        <DialogTitle>Abwesenheit gelöscht</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {(deleteResult?.restored?.length ?? 0) > 0
              ? `${deleteResult!.restored.length} ${
                  deleteResult!.restored.length === 1 ? 'Termin wurde' : 'Termine wurden'
                } wiederhergestellt.`
              : 'Es konnten keine Termine wiederhergestellt werden.'}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2 }}>
            Folgende abgesagte Termine bleiben abgesagt:
          </DialogContentText>
          <List dense>
            {deleteResult?.skipped?.map((skipped) => (
              <ListItem key={skipped.cancellationId} disableGutters>
                <ListItemText
                  primary={moment(skipped.originalDate).format('DD.MM.YYYY HH:mm')}
                  secondary={skipped.message}
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteResultDialog}>Schließen</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={hasConflict} disableEscapeKeyDown>
        <DialogTitle>Abwesenheiten wurden geändert</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ein anderer Benutzer hat die Abwesenheiten von {userData.firstName} {userData.lastName}{' '}
            {CONFLICT_ACTION_LABELS[conflict?.action ?? ''] ?? 'geändert'}
            {conflictPeriod && ` (${conflictPeriod})`}. Ihre Ansicht ist dadurch nicht mehr aktuell und das
            Speichern wurde gestoppt. Bitte laden Sie den Kalender neu, um mit den aktuellen Daten
            weiterzuarbeiten.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleReloadAfterConflict}>Kalender neu laden</Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UserCalendar